// Phase 12 — Trial + billing state machine (mocked, client-only)
import { create } from "zustand";
import { persist } from "zustand/middleware";

const MS_PER_DAY = 86_400_000;
const TRIAL_LEN_DAYS = 14;

// State machine derivation
function deriveState({ paidSince, trialStartDate, trialEndDate, demoOverride }) {
  if (demoOverride) return demoOverride;
  if (paidSince) return "paid";
  if (!trialStartDate || !trialEndDate) return "trial-active";
  const now = Date.now();
  const endMs = new Date(trialEndDate).getTime();
  if (now >= endMs) return "trial-expired";
  const daysLeft = Math.ceil((endMs - now) / MS_PER_DAY);
  return daysLeft <= 3 ? "trial-ending-soon" : "trial-active";
}

function deriveDaysLeft({ trialStartDate, trialEndDate, demoOverride }) {
  // For demo overrides, return a sensible mock count
  if (demoOverride === "trial-ending-soon") return 2;
  if (demoOverride === "trial-active") return 11;
  if (demoOverride === "trial-expired") return 0;
  if (demoOverride === "paid") return 0;
  if (!trialEndDate) return TRIAL_LEN_DAYS;
  const now = Date.now();
  const endMs = new Date(trialEndDate).getTime();
  return Math.max(0, Math.ceil((endMs - now) / MS_PER_DAY));
}

export const useBillingStore = create(
  persist(
    (set, get) => ({
      trialStartDate: null,
      trialEndDate: null,
      paidSince: null,
      currentPlan: "starter",
      cardOnFile: false,
      cardLast4: "",
      demoOverride: null, // 'trial-active' | 'trial-ending-soon' | 'trial-expired' | 'paid' | null
      invoices: [],
      recapModalSeen: false,

      initTrial: ({ withCard }) => {
        const now = new Date();
        const start = now.toISOString();
        const end = new Date(now.getTime() + TRIAL_LEN_DAYS * MS_PER_DAY).toISOString();
        set({
          trialStartDate: start,
          trialEndDate: end,
          paidSince: null,
          cardOnFile: !!withCard,
          cardLast4: withCard ? "4242" : "",
          demoOverride: null,
          recapModalSeen: false,
        });
      },

      setCardOnFile: (last4 = "4242") => set({ cardOnFile: true, cardLast4: last4 }),
      setPlan: (plan) => set({ currentPlan: plan }),

      upgrade: (plan = "starter") => {
        const now = new Date();
        const amount = { pro: 999, enterprise: 1499 }[plan] || 499;
        const id = `INV-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${String(Math.floor(Math.random() * 999)).padStart(3, "0")}`;
        const invoice = {
          id,
          dateLabel: now.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" }),
          amount,
          plan,
          status: "Paid",
        };
        set({
          paidSince: now.toISOString(),
          currentPlan: plan,
          cardOnFile: true,
          cardLast4: get().cardLast4 || "4242",
          demoOverride: null,
          invoices: [invoice, ...(get().invoices || [])],
          recapModalSeen: true, // suppress recap after upgrade
        });
      },

      setDemoState: (state) => set({
        demoOverride: state,
        // re-arm recap when re-entering ending-soon for stakeholder demos
        recapModalSeen: state === "trial-ending-soon" ? false : get().recapModalSeen,
      }),
      dismissRecap: () => set({ recapModalSeen: true }),
      resetBilling: () => set({ trialStartDate: null, trialEndDate: null, paidSince: null, cardOnFile: false, cardLast4: "", demoOverride: null, invoices: [], recapModalSeen: false }),
    }),
    { name: "sellix.billing.v1" },
  ),
);

// Selectors
export const useBillingState = () => useBillingStore((s) => deriveState(s));
export const useBillingDaysLeft = () => useBillingStore((s) => deriveDaysLeft(s));
export const useIsTrialExpired = () => useBillingStore((s) => deriveState(s) === "trial-expired");
